/**
 * FinishedProductHistoryModal — Transaction history modal for finished products.
 * Extracted from Dashboard.tsx for maintainability.
 */
import React from 'react';
import { X, Loader2 } from 'lucide-react';
import { InventoryTransaction } from '../../../services/inventoryService';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatDateTime as formatDateTimeUtil } from '../../../utils/dateUtils';
import { TX_TYPE_COLORS, getTxTypeLabels } from '../inventoryHelpers';

interface FinishedProductHistoryModalProps {
  item: {
    item_number: string;
    item_name: string;
    chinese_name?: string;
    is_byproduct_marketable?: boolean;
  };
  txData: InventoryTransaction[];
  txLoading: boolean;
  txPage: number;
  txTotal: number;
  txTotalPages: number;
  onLoadPage: (page: number) => void;
  onClose: () => void;
}

const FinishedProductHistoryModal: React.FC<FinishedProductHistoryModalProps> = ({
  item,
  txData,
  txLoading,
  txPage,
  txTotal,
  txTotalPages,
  onLoadPage,
  onClose,
}) => {
  const { t } = useLanguage();
  const formatDateTime = (dateString?: string) => formatDateTimeUtil(dateString);
  const txTypeColors = TX_TYPE_COLORS;
  const txTypeLabels = getTxTypeLabels(t);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
              {t('inventory.transactionHistory')}
            </h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-secondary-500 dark:text-gray-400">
              <p>
                {item.item_number} — {item.item_name || '-'}
                {item.chinese_name && ` (${item.chinese_name})`}
              </p>
              {item.is_byproduct_marketable && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {t('inventory.marketableByproduct')}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-gray-700 text-secondary-500 dark:text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {txLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-secondary-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('common.loading')}
            </div>
          ) : txData.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-secondary-500 dark:text-gray-400 uppercase border-b border-secondary-200 dark:border-gray-700">
                  <th className="pb-2 text-left">{t('dateTime.date')}</th>
                  <th className="pb-2 text-left">{t('inventory.transactionType')}</th>
                  <th className="pb-2 text-left">{t('inventory.boxLookupSession')}</th>
                  <th className="pb-2 text-right">{t('inventory.change')}</th>
                  <th className="pb-2 text-left pl-4">{t('inventory.boxLookupPacker')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100 dark:divide-gray-700">
                {txData.map((tx) => (
                  <tr key={tx.id}>
                    <td className="py-2 whitespace-nowrap text-secondary-900 dark:text-white">
                      {formatDateTime(tx.created_at)}
                    </td>
                    <td className="py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${txTypeColors[tx.transaction_type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {txTypeLabels[tx.transaction_type] || tx.transaction_type}
                      </span>
                    </td>
                    <td className="py-2 whitespace-nowrap text-primary-600 dark:text-primary-400">
                      {tx.session_number || '-'}
                    </td>
                    <td className={`py-2 whitespace-nowrap text-right font-semibold ${
                      (tx.transaction_type === 'case_cancelled' || tx.transaction_type === 'case_voided') ? 'text-gray-500 dark:text-gray-400'
                      : tx.transaction_type === 'case_pending' ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-green-600 dark:text-green-400'
                    }`}>
                      {tx.transaction_type === 'case_packed' ? '+' : ''}{Number(tx.quantity_change).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 pl-4 text-secondary-500 dark:text-gray-400">
                      {tx.created_by || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-8 text-secondary-500 dark:text-gray-400">{t('inventory.noTransactions')}</p>
          )}
        </div>

        {/* Footer with pagination */}
        <div className="px-6 py-3 border-t border-secondary-200 dark:border-gray-700 flex items-center justify-between">
          {txTotalPages > 1 ? (
            <>
              <p className="text-sm text-secondary-500 dark:text-gray-400">
                {t('common.showing')} {((txPage - 1) * 20) + 1}-{Math.min(txPage * 20, txTotal)} {t('common.of')} {txTotal}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onLoadPage(txPage - 1)}
                  disabled={txPage === 1}
                  className="px-3 py-1 text-sm border border-secondary-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  {t('common.previous')}
                </button>
                <button
                  onClick={() => onLoadPage(txPage + 1)}
                  disabled={txPage === txTotalPages}
                  className="px-3 py-1 text-sm border border-secondary-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  {t('common.next')}
                </button>
              </div>
            </>
          ) : (
            <div />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-gray-300 bg-secondary-100 dark:bg-gray-700 rounded-md hover:bg-secondary-200 dark:hover:bg-gray-600"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinishedProductHistoryModal;
